import { useEffect, useContext, useMemo } from "react";
import { UserContext } from "../../context/UserContext";
import { UserCtx, DbUser, CourseStructureCtx } from "../../interfaces";
import { Box, Grid } from "@mui/material";
import { HomeHeader } from "../../components/v2/home/Header";
import { MiniProfileCard } from "../../components/v2/home/MiniProfileCard";
import { CourseProgressCard } from "../../components/v2/home/CourseProgressCard";
import { StreakCard } from "../../components/v2/home/StreakCard";
import { InspirationQuoteCard } from "../../components/v2/home/InspirationQuote";
import { ModuleListContainer } from "../../components/v2/home/ModuleListContainer";
import { PageContainer } from "../../components/v2/PageContainer";
import { useHelpAndResources } from "../../hooks/useHelpAndResources";
import { useHistory } from "react-router-dom";
import axios from "../../services/axios";
import { logger } from "../../services/logger";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { generateGtmEvent } from "../../Helpers";
import { Question, Target } from "../../components/utils/CourseTree";
const patchDbUserSeenNewDesign = async (
  userId: String,
  data: Pick<DbUser, "seen_v2_design">
) => {
  try {
    await axios.patch("/user/seen_new_design", data);
  } catch (err) {
    logger.error(err);
  }
};

interface WelcomeCardData {
  backgroundColor: string;
  subheading: string;
  heading: string;
  content: string;
  buttonText: string;
  welcomeMsg: string;
  onClick: () => void;
}

export const HomePage = () => {
  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);
  const queryOpenTour =
    (queryParams.get("guidedtour") as string) === "true" ? true : false;

  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const currentUser: DbUser = userCtx.dbUser;
  const initials = (currentUser.first_name[0] + currentUser.last_name[0]).toUpperCase();
  const useDigitStudyTool = courseMetadata?.use_digit_study_tool ?? false;

  const { element: HelpAndResourcesButton, handleOpenGuidedTour } = useHelpAndResources({
    toolTipProps: {
      steps: [
        {
          header: "Getting started",
          body: "You can exit the tour and restart it anytime from this Help and Resources button. Use the button to see what resources are available to you.",
          position: "rightbottom",
          mountPosition: "left",
          targetId: "help-and-resources",
        },
        {
          header: "Your dashboard",
          body: "Browse the modules that make up the math tutoring experience any time here.",
          position: "topcenter",
          mountPosition: "bottom",
          targetId: "nav-home-button",
        },
        {
          header: "Module page",
          body: "Explore all of the learning targets and related lesson videos within a module by visiting its module page.",
          position: "bottomcenter",
          mountPosition: "top",
          targetId: "module-page-button",
        },
        {
          header: "Practice page",
          body: "Solve math problems and receive guidance by using the Practice button. You can also watch lesson videos within the Practice page for extra help.",
          position: "bottomcenter",
          mountPosition: "top",
          targetId: "practice-page-button",
        },
        {
          header: "Explore page",
          body: "Browse all of the lesson videos available to you and catch up on the ones you save throughout your experience in the Explore page.",
          position: "topcenter",
          mountPosition: "bottom",
          targetId: "nav-explore-button",
        },
        {
          header: "Let's start learning!",
          body: "If you need any help or support, send an email to digitsupport@asu.edu and we will get back to you in 24 hours.",
          position: "center",
          mountPosition: "centerscreen",
          targetId: "main",
        },
      ],
      parentId: "main",
    },
    showReminder: currentUser.user_progress ? true : false, // only display the reminder on home page if the user has previously started a problem
  });

  //// Commented out while we wait on confirmation that this is within scope
  //// Kelynn said they weren't sure if this feature will be necessary as its not lining up with the new year
  const existingUserWelcomeCardData = useMemo(() => {
    return {
      backgroundColor: "#80449F",
      subheading: "THINGS ARE LOOKING A BIT DIFFERENT...",
      heading: "We have a new look!",
      content:
        "A new year called for refreshing our style. Let us guide you through some of the new changes.",
      buttonText: "Launch Tour",
      welcomeMsg: "Hi, userName! Welcome back to Digit.",
      onClick: () => {
        generateGtmEvent({
          event: "link",
          action: "click",
          name: "onclick",
          type: "internal link",
          region: "main content",
          section: `We have a new look!`,
          text: "launch tour",
        });
        !useDigitStudyTool && handleOpenGuidedTour();
      },
    };
  }, [useDigitStudyTool, handleOpenGuidedTour]);

  const resumeCardData = useMemo<WelcomeCardData | null>(() => {
    const userProgress = currentUser.user_progress;
    const progressItem =
      userProgress && userProgress.length > 0 ? userProgress[0] : undefined;
    const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
    const allModules = currentCourse?.getAllModules() ?? [];

    if (allModules.length < 1) return null;
    const firstModule = allModules[0];
    const firstTarget = firstModule.children[0] as Target;
    const firstTargetName = firstTarget?.getFormattedTargetName() ?? "";
    const firstQuestion = firstTarget.getFirstPracticeProblem() as Question | undefined;
    const firstQuestionId = firstQuestion?.id ?? "";

    const resumeTarget = progressItem?.question.target;
    const resumeTargetName = resumeTarget?.name ?? "";
    const resumeQuestionId = progressItem?.question.id ?? "";

    const isProgressInCurrentCourse = allModules.some(
      (module) => module.id === resumeTarget?.module_id
    );

    const guidedTourEnabled =
      !courseMetadata?.help_and_resource_button?.disable_guided_tour;

    // resume
    if (isProgressInCurrentCourse) {
      return {
        backgroundColor: "#191919",
        subheading: "START FROM WHERE YOU LEFT OFF...",
        heading: resumeTargetName,
        content: resumeTarget?.target_page_summary ?? "",
        buttonText: "Resume learning",
        welcomeMsg: `Welcome back, ${currentUser.first_name}!`,
        onClick: () => {
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: resumeTargetName,
            text: "resume learning",
          });
          courseStructureCtx.setBackBehavior({ backTo: "home" });
          history.push({
            pathname: "/solver",
            search: "&questionId=" + resumeQuestionId,
          });
        },
      };
    } else {
      if (guidedTourEnabled && !useDigitStudyTool) {
        return {
          backgroundColor: "#80449F",
          subheading: "GET STARTED...",
          heading: "Guided Tour",
          content:
            "Welcome to Digit! This tour has six guided steps and will take you on a journey through the different parts of the tool.",
          buttonText: "Launch Tour",
          welcomeMsg: "Hi, userName! Welcome to Digit.",
          onClick: () => {
            generateGtmEvent({
              event: "modal",
              action: "open",
              name: "onclick",
              type: "click",
              region: "main content",
              section: `guided tour`,
              text: "launch tour",
            });
            handleOpenGuidedTour();
          },
        };
      } else {
        // new course start
        return {
          backgroundColor: "#191919",
          subheading: "BEGIN STUDYING HERE...",
          heading: firstTargetName,
          content: firstTarget?.target_page_summary ?? "",
          buttonText: "Begin learning",
          welcomeMsg: `Welcome to Digit, ${currentUser.first_name}!`,
          onClick: () => {
            generateGtmEvent({
              event: "link",
              action: "click",
              name: "onclick",
              type: "internal link",
              region: "main content",
              section: firstTargetName,
              text: "resume learning",
            });
            courseStructureCtx.setBackBehavior({ backTo: "home" });
            history.push({
              pathname: "/solver",
              search: "&questionId=" + firstQuestionId,
            });
          },
        };
      }
    }
  }, [
    currentUser,
    history,
    courseStructureCtx,
    courseMetadata?.help_and_resource_button?.disable_guided_tour,
    handleOpenGuidedTour,
    useDigitStudyTool,
  ]);

  const welcomeCardData = useMemo<WelcomeCardData | null>(() => {
    if (!currentUser) return null;
    const hasSeenNewDesign = currentUser.seen_v2_design;

    // 1. Show new design welcome if user hasn't seen it
    if (!hasSeenNewDesign) {
      patchDbUserSeenNewDesign(currentUser.id, { seen_v2_design: true });
      return existingUserWelcomeCardData;
    }

    return resumeCardData;
  }, [currentUser, existingUserWelcomeCardData, resumeCardData]);

  useEffect(() => {
    if (queryOpenTour) {
      handleOpenGuidedTour();
      // clears the search query
      history.push("/learning-overview");
    }
  }, [queryOpenTour, handleOpenGuidedTour, history]);

  return (
    <PageContainer>
      <Grid container spacing={10}>
        <Grid item xs={12} sm={8} component="section">
          <Box sx={{ maxWidth: 846, marginRight: "auto" }}>
            <HomeHeader
              userName={currentUser.full_name}
              renderData={welcomeCardData ?? existingUserWelcomeCardData}
              hideCard={!welcomeCardData}
            />
            <ModuleListContainer />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} component="section">
          <Box sx={{ maxWidth: 378, marginLeft: "auto" }}>
            <MiniProfileCard
              currentUser={currentUser}
              initials={initials}
              fullName={currentUser.full_name}
              school={
                currentUser.user_school.length > 0 && useDigitStudyTool !== true // No need to show school for digit as a study tool
                  ? currentUser.user_school[0]?.school?.name
                  : undefined
              }
              course={userCtx.studentCurrentClass?.course?.name ?? undefined}
            />
            <CourseProgressCard />
            {useDigitStudyTool !== true && (
              <>
                <StreakCard />
                <InspirationQuoteCard />
              </>
            )}
          </Box>
        </Grid>
      </Grid>
      {HelpAndResourcesButton}
    </PageContainer>
  );
};
